import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from "motion/react";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import { useAppStore } from '../stores/useAppStore';
import Button from './Button';
import IconSearch from './IconSearch';
import Keyboard from "react-simple-keyboard";
import "react-simple-keyboard/build/css/index.css";
import "./Search.css"; // Estilos personalizados para el teclado
import AnalyticsWrapper from './AnalyticsWrapper';
import useMenuNavigation from '../hooks/useMenuNavigation';
import { CAROUSEL_TYPES } from '../constants/carouselTypes';
import { useBrowserDetection } from '../hooks/useBrowserDetection';

// Función helper para acceder a campos anidados usando notación de punto
// Soporta: "title", "products.title", "data.items.name", etc.
// También maneja arrays de objetos: si "products" es un array y se accede a "products.title",
// extraerá el campo "title" de cada elemento del array
const getNestedValue = (obj, path) => {
    if (!path || !obj) return '';
    
    // Si no hay punto, es un campo simple
    if (!path.includes('.')) {
        return obj[path];
    }
    
    // Dividir el path por puntos y navegar por el objeto
    const keys = path.split('.');
    let value = obj;
    
    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        
        if (value == null) return '';
        
        value = value[key];
        
        // Si encontramos un array y no estamos en la última clave,
        // necesitamos mapear el resto del path en cada elemento del array
        if (Array.isArray(value) && i < keys.length - 1) {
            const remainingPath = keys.slice(i + 1).join('.');
            return value.map(item => getNestedValue(item, remainingPath)).filter(Boolean);
        }
    }
    
    return value;
};

export default function Search() {
    const { setSearchIsOpen, content } = useAppStore();
    const { openViewer } = useAppStore((state) => state.viewer);
    const { openCarousel } = useAppStore((state) => state.carousel);
    const [input, setInput] = useState("");
    const [layout, setLayout] = useState("default");
    const keyboard = useRef();
    const inputRef = useRef();
    const navigateTo = useMenuNavigation();
    const { isIOS, isSafariIOS } = useBrowserDetection();

    // Construir el índice de búsqueda dinámicamente desde sections
    const searchIndex = useMemo(() => {
        if (!content?.sections) return [];
        
        const index = [];
        
        content.sections.forEach(section => {
            if (!section.searchable || !section.searchConfig) return;
            
            const { dataKey, searchFields, carouselType } = section.searchConfig;
            let dataSource = content[dataKey];
            
            if (!dataSource) return;
            
            // Si el dataSource tiene una propiedad 'items', usar esa en lugar del objeto completo
            if (dataSource.items && Array.isArray(dataSource.items)) {
                dataSource = dataSource.items;
            }
            
            // Convertir a array si no lo es (para manejar objetos y arrays)
            const items = Array.isArray(dataSource) ? dataSource : [dataSource];
            
            items.forEach((item, itemIndex) => {
                // Si el item tiene productos, indexar cada producto individualmente
                if (item.products && Array.isArray(item.products)) {
                    item.products.forEach((product, productIndex) => {
                        
                        // Construir el texto de búsqueda para el producto
                        // Primero, obtener campos del item padre (que no sean de products)
                        const itemSearchText = searchFields
                            .filter(field => !field.startsWith('products'))
                            .map(field => {
                                const value = getNestedValue(item, field);
                                if (Array.isArray(value)) {
                                    return value.join(' ');
                                }
                                return value || '';
                            }).join(' ');
                        
                        // Luego, obtener campos del producto (products.*)
                        const productFieldsText = searchFields
                            .filter(field => field.startsWith('products.'))
                            .map(field => {
                                // Extraer el campo después de "products."
                                const productField = field.substring('products.'.length);
                                const value = getNestedValue(product, productField);
                                if (Array.isArray(value)) {
                                    return value.join(' ');
                                }
                                return value || '';
                            }).join(' ');
                        
                        // Combinar todo: campos del item + campos del producto + título del producto
                        const productSearchText = `${itemSearchText} ${productFieldsText} ${product.title || ''}`.toLowerCase();
                        
                        index.push({
                            // Datos del item padre
                            ...item,
                            // Metadata para la navegación
                            _meta: {
                                menuKey: section.key,
                                dataKey: dataKey,
                                carouselType: carouselType,
                                itemIndex: itemIndex,
                                productIndex: productIndex,
                                product: product, // Guardar el producto completo
                                searchText: productSearchText,
                                sectionTitle: section.title,
                                displayTitle: product.title || item.title, // Mostrar el título del producto
                                isProduct: true
                            }
                        });
                    });
                } else {
                    // Si no tiene productos, indexar el item normalmente
                    const searchText = searchFields.map(field => {
                        const value = getNestedValue(item, field);
                        if (Array.isArray(value)) {
                            return value.join(' ');
                        }
                        return value || '';
                    }).join(' ').toLowerCase();
                    
                    index.push({
                        // Datos del item
                        ...item,
                        // Metadata para la navegación
                        _meta: {
                            menuKey: section.key,
                            dataKey: dataKey,
                            carouselType: carouselType,
                            itemIndex: itemIndex,
                            searchText: searchText,
                            sectionTitle: section.title,
                            isProduct: false
                        }
                    });
                }
            });
        });
        
        return index;
    }, [content]);

    const filteredResults = useMemo(() => {
        if (input.length < 2) return [];
        
        const searchTerm = input.toLowerCase().trim();
        
        return searchIndex.filter(item => 
            item._meta.searchText.includes(searchTerm)
        );
    }, [input, searchIndex]);

    const noResults = filteredResults.length === 0;
    
    const variants = {
        closed: {
            backgroundColor: '#1a2951',
            clipPath: 'circle(4.875rem at calc(100% - 11.5rem) 11.5rem)',
            transition: {
                duration: 0.5,
                ease: "easeIn",
                delay: 0.1
            },
        },
        open: {
            backgroundColor: '#061933',
            clipPath: 'circle(140% at calc(100% - 11.5rem) 11.5rem)',
            transition: {
                duration: 0.7,
                ease: "easeOut",
                delay: 0.2
            },
        },
    };

    const keyboardVariants = {
        closed: { y: "100%", transition: { duration: 0.4, ease: "easeIn" } },
        open: { y: 0, transition: { duration: 0.5, ease: "easeOut", delay: 0.6 } }
    };
    
    const onChange = input => {
        setInput(input);
    };

    const onChangeInput = (event) => {
        const value = event.target.value;
        setInput(value); // Actualizar el estado con el valor del input físico
        // Solo sincronizar con el teclado virtual si existe (en iOS)
        if (keyboard.current && isIOS) {
            keyboard.current.setInput(value); // Sincronizar con el teclado virtual
        }
    };
    
    const onKeyPress = (button) => {
        if (button === "{shift}" || button === "{lock}") handleShift(); // Manejar el botón shift/caps lock
    };
    
    const handleShift = () => {
        const newLayoutName = layout === "default" ? "shift" : "default";
        setLayout(newLayoutName);
    };

    const handleResultClick = (result) => {
        const { product, isProduct, itemIndex, carouselType } = result._meta;
        
        setSearchIsOpen(false);  // Cierra el buscador

        // Si es un producto, abrir primero el carrusel y luego el visor
        if (isProduct && product) {
            // Abrir el carrusel en el índice del item padre
            if (carouselType) {
                openCarousel(itemIndex, CAROUSEL_TYPES[carouselType]);
            }
            
            // Luego abrir el visor con el producto
            setTimeout(() => {
                openViewer(product);
            }, 100); // Aumentar el delay para dar tiempo al carrusel a abrirse
        }
    };    useEffect(()=> {
        AnalyticsWrapper.trackPageView(`/search`);
    }, []);
    
    // Hacer focus en el input al montar el componente (solo cuando no en iOS)
    useEffect(() => {
        if (!isIOS && inputRef.current) {
            // Pequeño delay para asegurar que la animación no interfiera
            const timer = setTimeout(() => {
                inputRef.current.focus();
            }, 300);
            
            return () => clearTimeout(timer);
        }
    }, [isIOS]);
    
    return (
        
        <motion.div
            className='h-full w-full bg-navy fixed z-50 transform-gpu will-change-clip-path'
            variants={variants}
            initial="closed"
            animate="open"
            exit="closed"
        >
            <motion.div className={`
                relative
                w-[129.375rem] h-[12.75rem] mx-auto overflow-visible`}
                initial={{ marginTop: window.innerHeight / 2 - 51 }}
                animate={{ marginTop: noResults ? (window.innerHeight / 2) - 51 : 210 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}>
                <span className="
                    absolute
                    w-full
                    -top-[13rem]
                    font-extralight
                    text-[7.5rem]
                    text-center
                    leading-none">What are you looking for?</span>
                <div className={`
                        absolute
                        bg-navy
                        flex
                        items-center
                        justify-center
                        rounded-full
                        -z-10
                        ${isSafariIOS ? 'bg-gradient' : 'bg-gradient-animated'}
                        blur-md
                        w-full
                        h-full
                        `}></div>
                <div className={`
                    absolute
                    bg-navy
                    flex
                    items-center
                    justify-center
                    rounded-full
                    -z-10
                    ${isSafariIOS ? '' : 'bg-gradient-animated'}
                    w-full
                    h-full
                    `}></div>
                <Button
                    onClick={null}
                    className="absolute top-[1.5rem] right-[1.5rem] w-[9.75rem] h-[9.75rem] justify-center pointer-events-none">
                    <IconSearch className="w-[3.5rem] h-auto" />
                </Button>
                <input
                    ref={inputRef}
                    disabled={isIOS}
                    type="text"
                    placeholder="Start searching..."
                    value={input}
                    onChange={onChangeInput}
                    className={`
                        ml-[0.25rem]
                        mt-[0.25rem]
                        w-[calc(100%-0.5rem)]
                        h-[calc(100%-0.5rem)]
                        pl-[6rem] pr-[13.125rem]
                        rounded-full outline-none bg-navy text-white text-[3.375rem] font-extralight
                        ${isSafariIOS ? 
                            'placeholder:text-white'
                            : ''
                        }
                        `}
                />
                
                {/* Resultados */}
                <AnimatePresence>
                {input.length >= 2 && (
                    <motion.div
                    className="-z-20 -mt-[6rem] relative border-[0.25rem] border-[#e2e2e2] rounded-b-[4rem]"
                    initial={{ height: 0 }}
                    animate={{ height: 'auto' }}
                    exit={{ height: 0 }}
                    transition={{ duration: 0.5, ease: 'easeInOut' }}
                    style={{ overflow: 'hidden' }} // Para evitar que se vea el contenido mientras se anima la altura
                    >
                    <ScrollArea.Root 
                        type={filteredResults.length > 2 ? "always" : "auto"}
                        className="rounded-md shadow-md max-h-[58rem] overflow-hidden w-full"
                    >
                        <ScrollArea.Viewport 
                        className="p-[4rem] pt-[10rem] max-h-[58rem] overflow-y-auto touch-auto"
                        >
                        <AnimatePresence>
                            { !noResults ? (
                            filteredResults.map((result, index) => {
                                const isLast = index === filteredResults.length - 1;
                                
                                // Si es un producto, usar el título del producto, sino usar el título del item
                                const displayTitle = result._meta.isProduct 
                                    ? result._meta.displayTitle 
                                    : (result.title || result.summary || result.header || 'Untitled');
                                
                                const displayId = result.id || (index + 1);
                                
                                // Construir la línea de información: Nombre del apartado | Categoría (si existe)
                                const sectionName = result._meta.sectionTitle || '';
                                const category = result.category || '';
                                const displayInfo = category 
                                    ? `${sectionName} | ${category}` 
                                    : sectionName;
                                
                                return (
                                    <motion.div
                                        key={`${result._meta.menuKey}-${result._meta.itemIndex}-${result._meta.productIndex || 0}`}
                                        initial={{opacity: 0}}
                                        animate={{opacity: 1}}
                                        transition={{ duration: 0.3, delay: 0.1 * index }}
                                        onClick={() => handleResultClick(result)}
                                        className={`click-effect flex p-[5rem] gap-[3.125rem] rounded-[2rem] bg-[#061933] border-silver border-[0.25rem] ${isLast ? '' : 'mb-[1.875rem]'} items-center`}
                                    >
                                        {/*
                                        <span className={`flex flex-0 items-center justify-center rounded-full w-[6.625rem] h-[6.625rem] bg-[#03BCE9] font-light text-[3rem]`}>
                                            {displayId}
                                        </span>
                                        */}
                                        <div className='flex-1 font-extralight'>
                                            <p className="text-[3.375rem] mb-[1rem] leading-none">{displayTitle}</p>
                                            <p className="text-[2.5rem] leading-[3rem]">{displayInfo}</p>
                                        </div>
                                    </motion.div>
                                );
                            })
                            ) : (
                                <p className="text-[3.375rem] leading-none text-center">No results</p>
                            )}
                        </AnimatePresence>
                        </ScrollArea.Viewport>
                        <ScrollArea.Scrollbar 
                        orientation="vertical" 
                        className="w-6 bg-gray-200 rounded-full"
                        style={{ top: '10rem', bottom: '4rem' }}  // Hace que la barra respete el padding superior/inferior
                        >
                        <ScrollArea.Thumb className="bg-gray-400 rounded-full" />
                        </ScrollArea.Scrollbar>
                    </ScrollArea.Root>
                    </motion.div>
                )}
                </AnimatePresence>



            </motion.div>

            {/* Solo mostrar el teclado virtual en iOS */}
            {isIOS && (
                <motion.div
                    initial="closed"
                    animate="open"
                    exit="closed"
                    variants={keyboardVariants}
                    className="fixed bottom-[5.75rem] left-1/2 -translate-x-1/2"
                    style={{
                        WebkitTransform: 'translateX(-50%)',
                        transform: 'translateX(-50%)'
                    }}
                >
                    <Keyboard
                        keyboardRef={r => (keyboard.current = r)}
                        layoutName={layout}
                        onChange={onChange}
                        onKeyPress={onKeyPress}
                        theme="hg-theme-default hg-layout-default"
                        layout={{
                            default: [
                              "1 2 3 4 5 6 7 8 9 0 {bksp}",
                              "Q W E R T Y U I O P",
                              'A S D F G H J K L',
                              "Z X C V B N M",
                              "{space}"
                            ]
                          }}
                    />
                </motion.div>
            )}

        </motion.div>
    );
}